/**
 * AWS Cognito client for AskYourCash.
 *
 * Cognito is the identity provider: this module wraps amazon-cognito-identity-js
 * so the rest of the app never touches the SDK directly. Email/password uses SRP
 * (no redirect); Google/Apple use a redirect to the Cognito hosted domain, since
 * Cognito has no API to exchange a social token for Cognito tokens.
 *
 * All config comes from build-time REACT_APP_COGNITO_* vars (baked into the
 * bundle — see the frontend Dockerfile).
 */
import {
  CognitoUserPool,
  CognitoUser,
  CognitoUserSession,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  AuthenticationDetails,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';

const REGION = process.env.REACT_APP_COGNITO_REGION || '';
const USER_POOL_ID = process.env.REACT_APP_COGNITO_USER_POOL_ID || '';
const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || '';
const DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN || '';

/** True when the pool is configured — lets the UI degrade gracefully if not. */
export const isCognitoConfigured = (): boolean =>
  Boolean(REGION && USER_POOL_ID && CLIENT_ID);

const userPool = isCognitoConfigured()
  ? new CognitoUserPool({ UserPoolId: USER_POOL_ID, ClientId: CLIENT_ID })
  : null;

function pool(): CognitoUserPool {
  if (!userPool) {
    throw new Error('Cognito is not configured (REACT_APP_COGNITO_* missing).');
  }
  return userPool;
}

const cognitoUser = (email: string): CognitoUser =>
  new CognitoUser({ Username: email.toLowerCase(), Pool: pool() });

/** Normalise Cognito's error objects to a plain Error with a readable message. */
const asError = (err: unknown, fallback: string): Error => {
  const message =
    (err as { message?: string })?.message ||
    (typeof err === 'string' ? err : fallback);
  return new Error(message);
};

// ── Sign up ─────────────────────────────────────────────────────────────────

/**
 * Register a new user. Cognito emails a verification code; the account is not
 * usable until confirmSignUp() succeeds. Returns nothing on success.
 */
export function signUp(
  email: string,
  password: string,
  firstName: string,
  lastName: string,
): Promise<void> {
  const attributes = [
    new CognitoUserAttribute({ Name: 'email', Value: email.toLowerCase() }),
    new CognitoUserAttribute({ Name: 'given_name', Value: firstName }),
    new CognitoUserAttribute({ Name: 'family_name', Value: lastName }),
  ];
  return new Promise((resolve, reject) => {
    pool().signUp(email.toLowerCase(), password, attributes, [], (err) => {
      if (err) return reject(asError(err, 'Registration failed'));
      resolve();
    });
  });
}

/** Confirm a sign-up with the emailed verification code. */
export function confirmSignUp(email: string, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser(email).confirmRegistration(code, true, (err) => {
      if (err) return reject(asError(err, 'Confirmation failed'));
      resolve();
    });
  });
}

/** Re-send the verification code to a user who hasn't confirmed yet. */
export function resendConfirmationCode(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser(email).resendConfirmationCode((err) => {
      if (err) return reject(asError(err, 'Could not resend code'));
      resolve();
    });
  });
}

// ── Sign in (SRP) ─────────────────────────────────────────────────────────────

/**
 * Authenticate with email + password (SRP) and return the access token. Raises
 * an error tagged `code === 'UserNotConfirmedException'` when the account still
 * needs email verification, so the caller can route to the confirm screen.
 */
export function signIn(email: string, password: string): Promise<string> {
  const user = cognitoUser(email);
  const details = new AuthenticationDetails({
    Username: email.toLowerCase(),
    Password: password,
  });
  return new Promise((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: (session) => resolve(session.getAccessToken().getJwtToken()),
      onFailure: (err) => {
        const e = asError(err, 'Login failed') as Error & { code?: string };
        e.code = (err as { code?: string })?.code;
        reject(e);
      },
    });
  });
}

// ── Session (silent refresh) ────────────────────────────────────────────────

/**
 * Return a valid access token for the current user, transparently refreshing it
 * with the stored refresh token when the access token has expired. Resolves to
 * null when there is no signed-in user (or the refresh token is gone).
 *
 * The axios request interceptor calls this before every request so the API
 * always gets a fresh token without the app tracking expiry itself.
 */
export function getAccessToken(): Promise<string | null> {
  const user = userPool?.getCurrentUser();
  if (!user) return Promise.resolve(null);
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) return resolve(null);
      resolve(session.getAccessToken().getJwtToken());
    });
  });
}

/** Global sign-out: clears local tokens (the backend also calls GlobalSignOut). */
export function signOut(): void {
  userPool?.getCurrentUser()?.signOut();
}

// ── Password reset ────────────────────────────────────────────────────────────

/** Start a password reset — Cognito emails a code. */
export function forgotPassword(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser(email).forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(asError(err, 'Could not start password reset')),
    });
  });
}

/** Complete a password reset with the emailed code and a new password. */
export function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser(email).confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(asError(err, 'Could not reset password')),
    });
  });
}

// ── Social login (redirect) ───────────────────────────────────────────────────
//
// The hosted-UI redirect is a full-page navigation, so state that must survive
// the round trip (the CSRF `state` nonce, the PKCE verifier) is stashed in
// sessionStorage before we leave and read back in exchangeCodeForTokens.
const OAUTH_STATE_KEY = 'askyourcash_oauth_state';
const OAUTH_VERIFIER_KEY = 'askyourcash_oauth_pkce_verifier';

function randomUrlSafeString(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  window.crypto.getRandomValues(bytes);
  let binary = '';
  bytes.forEach((b) => { binary += String.fromCharCode(b); });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function pkceChallengeFor(verifier: string): Promise<string> {
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  const bytes = new Uint8Array(digest);
  let binary = '';
  bytes.forEach((b) => { binary += String.fromCharCode(b); });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Build the Cognito hosted-UI authorize URL for a federated provider and send
 * the browser there. Cognito redirects back to /auth/callback with a code that
 * the SDK exchanges for tokens.
 *
 * Includes a CSRF `state` nonce (verified in exchangeCodeForTokens — without
 * it, an attacker can plant their own authorization code in a victim's browser
 * via a crafted callback URL and log the victim into the attacker's account)
 * and a PKCE challenge (this is a public client with no client secret, so PKCE
 * is what proves the code redemption comes from the same browser that started
 * the flow).
 */
export async function redirectToSocialSignIn(provider: 'Google' | 'SignInWithApple'): Promise<void> {
  const redirectUri = `${window.location.origin}/auth/callback`;
  const state = randomUrlSafeString(24);
  const verifier = randomUrlSafeString(32);
  const challenge = await pkceChallengeFor(verifier);

  window.sessionStorage.setItem(OAUTH_STATE_KEY, state);
  window.sessionStorage.setItem(OAUTH_VERIFIER_KEY, verifier);

  const params = new URLSearchParams({
    identity_provider: provider,
    redirect_uri: redirectUri,
    response_type: 'code',
    client_id: CLIENT_ID,
    scope: 'openid email profile',
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  window.location.assign(`https://${DOMAIN}/oauth2/authorize?${params.toString()}`);
}

export const socialConfigured = (): boolean => Boolean(DOMAIN);

/**
 * Complete a social sign-in: validate the CSRF `state`, exchange the `code`
 * from /auth/callback for tokens (with the matching PKCE verifier) at the
 * Cognito token endpoint, then seed the SDK's local storage so
 * getCurrentUser()/getAccessToken() work exactly as they do after an SRP login.
 *
 * amazon-cognito-identity-js has no built-in redirect handler (that's an Amplify
 * feature), so we do the code exchange ourselves and hand the tokens to the SDK.
 * Returns the access token.
 */
export async function exchangeCodeForTokens(code: string, state: string | null): Promise<string> {
  const expectedState = window.sessionStorage.getItem(OAUTH_STATE_KEY);
  const verifier = window.sessionStorage.getItem(OAUTH_VERIFIER_KEY);
  window.sessionStorage.removeItem(OAUTH_STATE_KEY);
  window.sessionStorage.removeItem(OAUTH_VERIFIER_KEY);

  if (!expectedState || !state || state !== expectedState) {
    throw new Error('Sign-in request could not be verified (state mismatch). Please try again.');
  }
  if (!verifier) {
    throw new Error('Sign-in request could not be verified (missing PKCE verifier). Please try again.');
  }

  const redirectUri = `${window.location.origin}/auth/callback`;
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: CLIENT_ID,
    code,
    redirect_uri: redirectUri,
    code_verifier: verifier,
  });

  const resp = await fetch(`https://${DOMAIN}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!resp.ok) {
    throw new Error('Social sign-in failed to exchange the authorization code.');
  }
  const tokens = (await resp.json()) as {
    access_token: string;
    id_token: string;
    refresh_token: string;
  };

  // Reconstruct a CognitoUserSession and persist it so the SDK adopts this user.
  const session = new CognitoUserSession({
    AccessToken: new CognitoAccessToken({ AccessToken: tokens.access_token }),
    IdToken: new CognitoIdToken({ IdToken: tokens.id_token }),
    RefreshToken: new CognitoRefreshToken({ RefreshToken: tokens.refresh_token }),
  });
  // The username Cognito expects is the ID token's `cognito:username` claim.
  const username = session.getIdToken().payload['cognito:username'] as string;
  const user = new CognitoUser({ Username: username, Pool: pool() });
  user.setSignInUserSession(session);

  return tokens.access_token;
}
